import type { LoadFailure } from '../data/resource'
import { useStrings } from '../i18n'

/**
 * What the panel shows when it has no site to show: a skeleton while the
 * payload is in flight, and an honest error when it never arrives.
 */

const SKELETON_ROWS = ['address', 'operator', 'hours', 'station-a', 'station-b'] as const

export const DetailSkeleton = () => {
  const strings = useStrings()

  return (
    <div className="ev-skeleton" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{strings.common.loading}</span>
      <div className="ev-skeleton__title" />
      <div className="ev-skeleton__subtitle" />
      {SKELETON_ROWS.map((row) => (
        <div key={row} className="ev-skeleton__block" />
      ))}
    </div>
  )
}

/**
 * A failed detail load names what went wrong and offers a retry, rather than
 * leaving an empty panel that looks like the site simply has no data.
 *
 * The headline comes from the failure's kind, so a publish that does not
 * validate says so instead of blaming the visitor's connection. Retrying a 404
 * is not offered: the site is gone, and pressing the button again will not
 * bring it back.
 */
export const DetailError = ({
  failure,
  onRetry,
}: {
  failure: LoadFailure
  onRetry: () => void
}) => {
  const strings = useStrings()

  return (
    <div className="ev-error" role="alert">
      <p className="ev-error__title">
        {failure.notFound ? strings.detail.notFound : strings.dataHealth.failure[failure.kind]}
      </p>
      <p className="ev-error__hint">
        {failure.notFound
          ? strings.detail.loadFailedHint
          : strings.dataHealth.failureHint[failure.kind]}
      </p>
      {failure.detail !== null && (
        <details className="ev-error__details">
          <summary>{strings.dataHealth.technicalDetail}</summary>
          <p className="ev-error__detail">{failure.detail}</p>
        </details>
      )}
      {!failure.notFound && (
        <button type="button" className="ev-button" onClick={onRetry}>
          {strings.common.retry}
        </button>
      )}
    </div>
  )
}

export const DetailEmpty = () => {
  const strings = useStrings()
  return <p className="ev-empty">{strings.detail.empty}</p>
}
